import type { ParameterDetail } from "@/features/line-editor/hooks";
import type {
  LossTypeValue,
  ParameterCategory,
  ProductionRole,
  StatusMappingEntry,
} from "@/types/parameters.types";
import { CATEGORY_SOURCES, mappingObjectToArray } from "@/types/parameters.types";

export type SourceKind = ParameterDetail["source"];

export interface ParamForm {
  category: ParameterCategory | null;
  name: string;
  unit: string;
  source: SourceKind;
  statusMapping: StatusMappingEntry[];
  isRestriction: boolean;
  // Spec 081 — o que o contador mede. Só COUNTER; o sentido é imutável após criar. Strings vazias
  // em vez de null porque o estado do formulário espelha o `value` do campo, não o do banco.
  productionRole: "" | ProductionRole;
  productId: string;
  lossType: "" | LossTypeValue;
  unitFactor: string;
  generatesAlarm: boolean; // Só STATUS; default marcado
  description: string;
  // Spec 078 — papel na cena 3D (COUNTER: FLOW; NUMERIC: TEMPERATURE) e limites (só NUMERIC)
  visualRole: "NONE" | "FLOW" | "TEMPERATURE";
  limitMax: string;
  limitMin: string;
}

export const EMPTY_FORM: ParamForm = {
  category: null,
  name: "",
  unit: "",
  source: "MANUAL",
  statusMapping: [],
  isRestriction: false,
  productionRole: "",
  productId: "",
  lossType: "",
  unitFactor: "",
  generatesAlarm: true,
  description: "",
  visualRole: "NONE",
  limitMax: "",
  limitMin: "",
};

export function emptyFormFor(category: ParameterCategory): ParamForm {
  const allowedSources = CATEGORY_SOURCES[category];
  return {
    category,
    name: "",
    unit: category === "DURATION" ? "s" : "",
    source: allowedSources[0] ?? "MANUAL",
    statusMapping: [],
    isRestriction: false,
    productionRole: "",
    productId: "",
    lossType: "",
    unitFactor: "",
    generatesAlarm: true,
    description: "",
    visualRole: "NONE",
    limitMax: "",
    limitMin: "",
  };
}

export function formFromParameter(p: ParameterDetail): ParamForm {
  const category: ParameterCategory = p.category ?? "NUMERIC";
  return {
    category,
    name: p.name,
    unit: p.unit ?? "",
    source: p.source,
    statusMapping: mappingObjectToArray(p.statusMapping),
    isRestriction: p.isRestriction,
    productionRole: p.productionRole ?? "",
    productId: p.productId ?? "",
    lossType: p.lossType ?? "",
    // Só a SOBREPOSIÇÃO volta para o campo. A equivalência herdada do produto é mostrada como
    // texto de ajuda; trazê-la para cá gravaria uma sobreposição que o gestor nunca pediu.
    unitFactor: p.unitFactorIsOverride === true ? String(p.effectiveUnitFactor ?? "") : "",
    generatesAlarm: p.generatesAlarm,
    description: p.description ?? "",
    visualRole: p.visualRole ?? "NONE",
    limitMax: p.limitMax === null || p.limitMax === undefined ? "" : String(p.limitMax),
    limitMin: p.limitMin === null || p.limitMin === undefined ? "" : String(p.limitMin),
  };
}
